import { env } from '@/lib/env';
import { getAccessToken } from '@/services/authTokenStorage';
import { isSilentRefreshEligible, refreshAccessTokenOnce } from '@/services/sessionRefresh';
import { isCurrentSessionOrigin } from '@/services/sessionOrigin';
import { containsSimulatedData } from './productionDataPolicy';

// Shared fetch wrapper. On the happy path it returns typed data; on any
// network/parse failure it returns a structured, non-throwing result so UI
// callers can render explicit unavailable states when the API is down.

export interface ApiResult<T> {
  ok: boolean;
  status: number;
  data: T | null;
  error: string | null;
  errorCode?: string | null;
}

export interface HealthStatus {
  status: 'ok' | 'degraded' | 'down';
  database: 'up' | 'down' | 'skipped' | 'unknown';
  objectStorage: 'up' | 'down' | 'skipped' | 'unknown';
  uptime: number;
  timestamp: string;
  reachable: boolean;
}

interface HealthResponse {
  status: 'ok' | 'degraded';
  uptime: number;
  timestamp: string;
  checks?: {
    database?: 'up' | 'down' | 'skipped';
    redis?: 'up' | 'down' | 'skipped';
    objectStorage?: 'up' | 'down' | 'skipped';
  };
}

export interface DashboardBootstrap {
  tenant: { id: string; name: string } | null;
  navigation: Array<{ id: string; label: string; path: string }>;
  featureFlags: Record<string, boolean>;
  systemStatus: {
    api: string;
    database: 'up' | 'down';
    websocket: string;
    environment: string;
    generatedAt: string;
  };
  engineeringBible: { present: boolean; version: string };
  counts: {
    projects: number | null;
    alerts: number | null;
    agents: number | null;
    notifications: number | null;
    portfolioValue: number | null;
  };
}

interface ApiEnvelope {
  success: boolean;
  data?: unknown;
  error?: {
    message?: string;
    code?: string;
  };
}

interface UnwrappedBody<T> {
  data: T | null;
  error: string | null;
  errorCode?: string | null;
}

// The API's global interceptor wraps responses as { success, data|error }.
// Unwrap defensively, tolerating an already-unwrapped body.
function unwrapEnvelope<T>(body: unknown): UnwrappedBody<T> {
  if (body && typeof body === 'object' && 'success' in (body as Record<string, unknown>)) {
    const envelope = body as ApiEnvelope;
    if (!envelope.success) {
      return {
        data: null,
        error: envelope.error?.message ?? 'API request failed',
        errorCode: envelope.error?.code ?? null,
      };
    }
    return { data: (envelope.data as T) ?? null, error: null };
  }
  return { data: (body as T) ?? null, error: null };
}

const trimEnd = (value: string): string => value.replace(/\/+$/, '');

export const apiBaseUrl = trimEnd(env.apiBaseUrl);

export function resolveApiOrigin(baseUrl: string, browserOrigin?: string): string {
  try {
    return new URL(baseUrl, browserOrigin || 'http://localhost').origin;
  } catch {
    return browserOrigin || 'http://localhost:4000';
  }
}

export const apiOrigin = resolveApiOrigin(
  env.apiBaseUrl,
  typeof window !== 'undefined' ? window.location.origin : undefined,
);

export function resolveRequestUrl(baseUrl: string, path: string): string {
  if (/^https?:\/\//.test(path)) return path;

  const normalizedBase = trimEnd(baseUrl);
  const normalizedPath = `/${path.replace(/^\/+/, '')}`;

  // Some feature clients use version-rooted paths (`/v1/...`) while the
  // configured base already ends in `/api/v1`. Preserve one version segment
  // for both same-origin and absolute API bases.
  if (
    normalizedBase.endsWith('/v1') &&
    (normalizedPath === '/v1' || normalizedPath.startsWith('/v1/'))
  ) {
    return `${normalizedBase.slice(0, -3)}${normalizedPath}`;
  }

  if (
    normalizedBase.startsWith('/') &&
    (normalizedPath === normalizedBase || normalizedPath.startsWith(`${normalizedBase}/`))
  ) {
    return normalizedPath;
  }

  try {
    const parsedBase = new URL(normalizedBase);
    if (
      normalizedPath === parsedBase.pathname ||
      normalizedPath.startsWith(`${trimEnd(parsedBase.pathname)}/`)
    ) {
      return `${parsedBase.origin}${normalizedPath}`;
    }
  } catch {
    // A relative base falls through to the normal join below.
  }

  return `${normalizedBase}${normalizedPath}`;
}

function resolve(path: string): string {
  return resolveRequestUrl(apiBaseUrl, path);
}

async function request<T>(
  path: string,
  init?: RequestInit,
  allowRefresh = true,
  options?: RequestOptions,
): Promise<ApiResult<T>> {
  const fenced = options !== undefined && 'sessionOrigin' in options;
  const origin = options?.sessionOrigin ?? null;
  const changed = (): ApiResult<T> => ({
    ok: false,
    status: 409,
    data: null,
    error: 'Your sign-in changed. Review the current page before trying again.',
    errorCode: 'REQUEST_ORIGIN_CHANGED',
  });
  if (fenced && !isCurrentSessionOrigin(origin)) return changed();
  if (fenced) {
    const explicitAuthorization = new Headers(init?.headers).get('Authorization');
    if (explicitAuthorization !== null && explicitAuthorization !== `Bearer ${getAccessToken()}`) {
      return changed();
    }
  }
  const result = await dispatch<T>(path, init);
  if (fenced && !isCurrentSessionOrigin(origin)) return changed();
  if (allowRefresh && result.status === 401 && isSilentRefreshEligible(path)) {
    const refreshed = await refreshAccessTokenOnce(fenced ? origin : undefined);
    if (fenced && !isCurrentSessionOrigin(origin)) return changed();
    if (refreshed.ok) {
      return request<T>(path, init, false, options);
    }
  }
  return result;
}

async function dispatch<T>(path: string, init?: RequestInit): Promise<ApiResult<T>> {
  try {
    const token = getAccessToken();
    const headers = new Headers(init?.headers);
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(resolve(path), {
      ...init,
      credentials: 'include',
      headers,
    });

    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }

    if (!response.ok) {
      const unwrapped = unwrapEnvelope<T>(body);
      return {
        ok: false,
        status: response.status,
        data: null,
        error: unwrapped.error ?? (response.statusText || 'API request failed'),
        ...(unwrapped.errorCode ? { errorCode: unwrapped.errorCode } : {}),
      };
    }

    const unwrapped = unwrapEnvelope<T>(body);
    if (unwrapped.error) {
      return { ok: false, status: response.status, data: null, error: unwrapped.error };
    }

    if (import.meta.env.PROD && containsSimulatedData(unwrapped.data)) {
      return {
        ok: false,
        status: 503,
        data: null,
        error: 'Data unavailable: this response contains simulated data prohibited in production.',
        errorCode: 'PRODUCTION_SIMULATED_DATA_BLOCKED',
      };
    }

    return { ok: true, status: response.status, data: unwrapped.data, error: null };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network request failed';
    return { ok: false, status: 0, data: null, error: message };
  }
}

async function get<T>(path: string, options?: RequestOptions): Promise<ApiResult<T>> {
  return request<T>(
    path,
    { method: 'GET', headers: options?.headers, signal: options?.signal },
    options?.allowRefresh ?? true,
    options,
  );
}

/** Optional per-request headers (e.g. `Idempotency-Key`). Auth and content-type stay automatic. */
export interface RequestOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Presence makes this request fail closed when its initiating sign-in changes. */
  sessionOrigin?: string | null;
  /** Explicit decisions can surface an expired session without refreshing or replaying the write. */
  allowRefresh?: boolean;
}

async function post<T, B = unknown>(
  path: string,
  body?: B,
  options?: RequestOptions,
): Promise<ApiResult<T>> {
  return request<T>(
    path,
    {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
      headers: options?.headers,
      signal: options?.signal,
    },
    options?.allowRefresh ?? true,
    options,
  );
}

async function patch<T, B = unknown>(
  path: string,
  body?: B,
  options?: RequestOptions,
): Promise<ApiResult<T>> {
  return request<T>(
    path,
    {
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
      headers: options?.headers,
      signal: options?.signal,
    },
    options?.allowRefresh ?? true,
    options,
  );
}

async function put<T, B = unknown>(
  path: string,
  body?: B,
  options?: RequestOptions,
): Promise<ApiResult<T>> {
  return request<T>(
    path,
    {
      method: 'PUT',
      body: body === undefined ? undefined : JSON.stringify(body),
      headers: options?.headers,
      signal: options?.signal,
    },
    options?.allowRefresh ?? true,
    options,
  );
}

async function del<T, B = unknown>(
  path: string,
  options?: RequestOptions,
  body?: B,
): Promise<ApiResult<T>> {
  return request<T>(
    path,
    {
      method: 'DELETE',
      headers: options?.headers,
      signal: options?.signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    options?.allowRefresh ?? true,
    options,
  );
}

async function checkHealth(): Promise<HealthStatus> {
  const result = await get<unknown>(`${apiOrigin}/api/health`);
  const health = result.ok ? (result.data as HealthResponse | null) : null;

  if (!health) {
    return {
      status: 'down',
      database: 'unknown',
      objectStorage: 'unknown',
      uptime: 0,
      timestamp: new Date().toISOString(),
      reachable: false,
    };
  }

  return {
    status: health.status,
    database: health.checks?.database ?? 'unknown',
    objectStorage: health.checks?.objectStorage ?? 'unknown',
    uptime: health.uptime,
    timestamp: health.timestamp,
    reachable: true,
  };
}

async function getBootstrap(options?: RequestOptions): Promise<DashboardBootstrap | null> {
  const result = await get<unknown>(`${apiOrigin}/api/dashboard/bootstrap`, options);
  if (!result.ok) return null;
  return result.data as DashboardBootstrap | null;
}

export const apiClient = {
  get,
  post,
  patch,
  put,
  delete: del,
  checkHealth,
  getBootstrap,
  resolve,
};
